import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton } from '../atoms/IconButton';
import { useAuthContext } from '../../contexts/AuthContext';

/**
 * ProfileMenu Component - Molecular Level
 * 
 * User profile dropdown menu for header
 * 
 * Features:
 * - User avatar and info
 * - Dropdown menu with actions
 * - Click outside to close
 * - Keyboard navigation
 * - Accessible
 */

export interface ProfileMenuProps {
  /** Custom menu items */
  menuItems?: Array<{
    label: string;
    icon?: React.ReactNode;
    onClick: () => void;
    variant?: 'default' | 'danger';
  }>;
}

export const ProfileMenu: React.FC<ProfileMenuProps> = ({ menuItems: customMenuItems }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { user, logout } = useAuthContext();

  // Helper functions to handle different user profile types
  const getUserName = (user: unknown) => {
    if (!user || typeof user !== 'object') return 'User';
    
    // Check if it's a User type (has firstName/lastName)
    if ('firstName' in user && 'lastName' in user) {
      const userObj = user as { firstName?: string; lastName?: string; fullName?: string };
      return `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() || userObj.fullName || 'User';
    }
    
    // For MerchantProfile or AdminProfile, use fullName
    const userObj = user as { username?: string };
    return userObj.username || 'User';
  };

  const getUserRole = (user: unknown) => {
    if (!user || typeof user !== 'object') return undefined;
    
    // Check if it's a User type (has role)
    if ('role' in user) {
      const userObj = user as { role?: string };
      return userObj.role;
    }
    
    // For AdminProfile, use accessLevel
    if ('accessLevel' in user) {
      const userObj = user as { accessLevel?: string };
      return userObj.accessLevel;
    }
    
    // For MerchantProfile, default to MERCHANT
    return 'MERCHANT';
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Close menu on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const defaultMenuItems = [
    {
      label: 'My Profile',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
          />
        </svg>
      ),
      onClick: () => {
        navigate('/profile');
        setIsOpen(false);
      },
    },
    // {
    //   label: 'Settings',
    //   icon: (
    //     <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    //       <path
    //         strokeLinecap="round"
    //         strokeLinejoin="round"
    //         strokeWidth={2}
    //         d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    //       />
    //       <path
    //         strokeLinecap="round"
    //         strokeLinejoin="round"
    //         strokeWidth={2}
    //         d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
    //       />
    //     </svg>
    //   ),
    //   onClick: () => {
    //     navigate('/admin/settings');
    //     setIsOpen(false);
    //   },
    // },
    {
      label: 'Sign Out',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
          />
        </svg>
      ),
      onClick: handleLogout,
      variant: 'danger' as const,
    },
  ];

  const menuItems = customMenuItems || defaultMenuItems;

  return (
    <div className="relative" ref={menuRef}>
      <IconButton
        icon={
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
        }
        ariaLabel="User menu"
        variant="ghost"
        onClick={() => setIsOpen(!isOpen)}
      />

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50"
          role="menu"
        >
          {/* User Info */}
          <div className="px-4 py-3 border-b border-gray-200">
            <p className="text-sm font-semibold text-gray-900">
              {getUserName(user)}
            </p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            {getUserRole(user) && (
              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-[#EAB308]/10 text-[#EAB308] rounded">
                {getUserRole(user)}
              </span>
            )}
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {menuItems.map((item, index) => (
              <button
                key={index}
                onClick={item.onClick}
                className={`
                  w-full flex items-center gap-3 px-4 py-2 text-sm transition-colors
                  ${
                    item.variant === 'danger'
                      ? 'text-red-600 hover:bg-red-50'
                      : 'text-gray-700 hover:bg-gray-100'
                  }
                `}
                role="menuitem"
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileMenu;

